import React, { useEffect, useState } from 'react'
import { Dialog } from '@progress/kendo-react-dialogs'
import { createPortal } from 'react-dom'
import CLOSE_ICON from '@public/assets/close_dialog.svg'
import DIALOG_BAR_IMAGE from '@public/assets/common/dialog_confirmational.svg'
import CHECKBOX_SELECTED from '@public/assets/mobile/checkbox_selected.svg'
import CHECKBOX_UNSELECTED from '@public/assets/mobile/checkbox_unselected.svg'
import '@css/HeartBeatDialog.css'

interface ExcludeConfirmDialogProps {
  isMobile?: boolean
  loading?: boolean
  onClose: () => void
  onConfirm: (deleteOutstanding: boolean, values: string[]) => void
  sender: string | string[]
}

export function ExcludeConfirmDialog({
  isMobile = false,
  loading = false,
  onClose,
  onConfirm,
  sender,
}: ExcludeConfirmDialogProps) {
  const [deleteOutstanding, setDeleteOutstanding] = useState(false)
  const senderList = Array.isArray(sender) ? sender : [sender]
  const isMultiSender = senderList.length > 1
  const [selectedSenders, setSelectedSenders] = useState<string[]>([])
  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (selectedSenders.length > 0) {
      setShowError(false)
    }
  }, [selectedSenders])

  return createPortal(
    <Dialog
      onClose={() => {
        if (!loading) onClose()
      }}
      className={`heartbeat-dialog ${
        isMobile ? 'mobile-heartbeat-dialog' : ''
      }`}
    >
      <button
        disabled={loading}
        onClick={event => {
          event.stopPropagation()
          if (!loading) onClose()
        }}
        onMouseEnter={event => event.stopPropagation()}
        onMouseLeave={event => event.stopPropagation()}
        onMouseMove={event => event.stopPropagation()}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#999',
          opacity: loading ? 0.5 : 1,
        }}
        aria-label="Close"
      >
        <img src={CLOSE_ICON} alt="icon" />
      </button>
      <div
        className="heartbeat-card"
        onClick={e => e.stopPropagation()}
        onMouseMove={e => e.stopPropagation()}
        onMouseEnter={e => e.stopPropagation()}
        onMouseLeave={e => e.stopPropagation()}
      >
        {/* 顶部 bar */}
        <div className="heartbeat-image">
          <img src={DIALOG_BAR_IMAGE} alt="" />
        </div>

        {/* 内容 */}
        <div className="heartbeat-content">
          <h2>
            {isMultiSender
              ? 'Inbound Email Filter Set for Senders Below'
              : `Inbound Email Filter Set for ${sender}`}
          </h2>

          <p
            className="heartbeat-description"
            style={{ marginBottom: 20, fontSize: 14 }}
          >
            You can view the filtered list or remove this filter at any time
            from Settings in the left-hand menu, where you download the desktop
            agent.
          </p>

          {isMultiSender && (
            <div style={{ marginTop: 12, position: 'relative' }}>
              {/* 浮动标题 */}
              <div
                style={{
                  position: 'absolute',
                  top: -10,
                  left: 10,
                  background: '#fff',
                  padding: '0 6px',
                  fontSize: 12,
                  color: '#666',
                }}
              >
                <span style={{ color: '#E25750' }}>*</span>
                Sender
              </div>
              <div
                style={{
                  border: `1px solid ${showError ? '#B3261E' : '#D9D9D9'}`,
                  borderRadius: 8,
                  padding: '12px 8px',
                  maxHeight: 120,
                  overflowY: 'auto',
                }}
              >
                {senderList.map((s, idx) => (
                  <div
                    key={`${s}-${idx}`}
                    onClick={() => {
                      if (loading) return
                      setSelectedSenders(prev => {
                        const exists = prev.includes(s)
                        if (exists) return prev.filter(x => x !== s)
                        return [...prev, s]
                      })
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      opacity: loading ? 0.6 : 1,
                      userSelect: 'none',
                      padding: '4px 0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        boxSizing: 'border-box',
                        padding: 4,
                      }}
                    >
                      <img
                        src={
                          selectedSenders.includes(s)
                            ? CHECKBOX_SELECTED
                            : CHECKBOX_UNSELECTED
                        }
                        alt="checkbox"
                        style={{
                          pointerEvents: 'none',
                          width: 14,
                          height: 14,
                          display: 'block',
                        }}
                      />
                    </div>

                    <span>{s}</span>
                  </div>
                ))}
              </div>
              {showError && (
                <div
                  style={{
                    color: '#e53935',
                    fontSize: 12,
                    marginTop: 4,
                    textAlign: 'left',
                  }}
                >
                  Please select at least one.
                </div>
              )}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 8,
              fontSize: 14,
              color: '#333',
              marginTop: 20,
              paddingLeft: 10,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                width: 24,
                height: 24,
                boxSizing: 'border-box',
                padding: 4,
                opacity: loading ? 0.6 : 1,
              }}
              onClick={() => {
                if (loading) return
                setDeleteOutstanding(prev => !prev)
              }}
            >
              <img
                src={
                  deleteOutstanding ? CHECKBOX_SELECTED : CHECKBOX_UNSELECTED
                }
                alt="group checkbox"
                style={{
                  pointerEvents: 'none',
                  width: 14,
                  height: 14,
                  display: 'block',
                }}
              />
            </div>
            <div style={{ textAlign: 'left' }}>
              Delete any outstanding captured emails from this sender that have
              not yet been accepted as time entries.
            </div>
          </div>
        </div>

        {/* 按钮 */}
        <div className="heartbeat-footer" style={{ display: 'flex', gap: 8 }}>
          <button
            className="cancel-button"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className="confirm-button primary-button"
            disabled={loading}
            aria-busy={loading}
            onClick={() => {
              if (loading) return
              if (isMultiSender && selectedSenders.length === 0) {
                setShowError(true)
                return
              }

              setShowError(false)

              onConfirm(
                deleteOutstanding,
                isMultiSender ? selectedSenders : senderList
              )
            }}
          >
            {loading ? 'Processing…' : 'Confirm'}
          </button>
        </div>
      </div>
    </Dialog>,
    document.body
  )
}
